import React from 'react';

interface WealthyLogoProps {
  size?: number;
  className?: string;
  showText?: boolean;
  textClassName?: string;
}

export const WealthyLogo: React.FC<WealthyLogoProps> = ({ 
  size = 28, 
  className = '',
  showText = false,
  textClassName = ''
}) => {
  return (
    <div className={`inline-flex items-center gap-3.5 select-none ${className}`}>
      <div 
        className="relative flex items-center justify-center shrink-0 rounded-2xl shadow-lg transition-transform duration-300 hover:scale-105 active:scale-95"
        style={{
          width: size + 16,
          height: size + 16,
          background: 'linear-gradient(135deg, #090D16 0%, #172033 50%, #0B1120 100%)',
          boxShadow: '0 10px 24px -4px rgba(11, 17, 32, 0.45), inset 0 1px 1px rgba(255, 255, 255, 0.25), inset 0 -1px 2px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Subtle dynamic backdrop glow */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-75 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 35% 25%, rgba(56, 189, 248, 0.4) 0%, rgba(16, 185, 129, 0.15) 55%, transparent 75%)'
          }}
        />
        
        {/* Bespoke Geometric 'W' Wealth Monogram Vector */}
        <svg 
          width={size} 
          height={size} 
          viewBox="0 0 32 32" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className="relative z-10"
        >
          <defs>
            <linearGradient id="wealthyWingLeft" x1="4" y1="6" x2="15" y2="26" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#38BDF8" />
              <stop offset="100%" stopColor="#2563EB" />
            </linearGradient>
            <linearGradient id="wealthyWingRight" x1="16" y1="5" x2="28" y2="25" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#34D399" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <linearGradient id="wealthyCenterKeystone" x1="16" y1="8" x2="16" y2="22" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#CBD5E1" />
            </linearGradient>
            <linearGradient id="wealthyBeamGlow" x1="16" y1="5" x2="16" y2="27" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#34D399" stopOpacity="0.1" />
            </linearGradient>
          </defs>

          {/* Left Wing / Velocity Pillar */}
          <path 
            d="M5 8.5C5 7.67 5.67 7 6.5 7H9.2C9.8 7 10.34 7.36 10.57 7.92L14.2 17.5L12.3 22.8C12.02 23.6 11.15 24 10.35 23.7C9.9 23.5 9.55 23.15 9.4 22.7L5.2 9.8C5.07 9.4 5 9 5 8.5Z" 
            fill="url(#wealthyWingLeft)" 
          />

          {/* Right Wing / Compounding Capital Peak (Ascending higher) */}
          <path 
            d="M27 6.5C27 5.67 26.33 5 25.5 5H22.8C22.2 5 21.66 5.36 21.43 5.92L17.8 15.5L19.7 20.8C19.98 21.6 20.85 22 21.65 21.7C22.1 21.5 22.45 21.15 22.6 20.7L26.8 7.8C26.93 7.4 27 7 27 6.5Z" 
            fill="url(#wealthyWingRight)" 
          />

          {/* Center Precision Keystone */}
          <path 
            d="M16 8.5L19.8 18.8C20.1 19.6 19.5 20.4 18.7 20.4H13.3C12.5 20.4 11.9 19.6 12.2 18.8L16 8.5Z" 
            fill="url(#wealthyCenterKeystone)" 
          />

          {/* Ascending Apex Beacon Spark */}
          <circle cx="25.5" cy="5" r="1.75" fill="#38BDF8" />
          <circle cx="25.5" cy="5" r="3" fill="#38BDF8" fillOpacity="0.25" />
        </svg>
      </div>

      {showText && (
        <span className={`text-2xl font-black tracking-tighter leading-tight text-slate-900 dark:text-white font-sans ${textClassName}`}>
          Wealthy
        </span>
      )}
    </div>
  );
};

export default WealthyLogo;
